import React, { useEffect, useRef, useState } from "react";
import { Pencil } from "lucide-react";
import { useEditMode } from "../lib/EditModeContext";
import { toast } from "sonner";

/**
 * EditableText — inline-editable text used everywhere across OPERATOR OS.
 *
 * Activation:
 *   - Double-click the text.
 *   - When Edit Mode (global toggle) is ON, hover pencil is always visible;
 *     clicking the pencil OR clicking the text enters edit mode.
 *   - When Edit Mode is OFF, a subtle pencil appears on hover.
 *
 * Save on:
 *   - Enter (single-line) / Cmd+Enter (multiline)
 *   - Blur
 *
 * Cancel on:
 *   - Escape
 */
const EditableText = ({
  value,
  onSave,
  as: Tag = "span",
  className = "",
  inputClassName = "",
  multiline = false,
  placeholder = "—",
  emptyPlaceholder = "—",
  testid,
  disabled = false,
  displayTransform,
  saveNotice,
}) => {
  const { editMode } = useEditMode();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? "");
  const [saving, setSaving] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!editing) setDraft(value ?? "");
  }, [value, editing]);

  useEffect(() => {
    if (editing && ref.current) {
      ref.current.focus();
      // Move caret to end
      const el = ref.current;
      const len = (el.value ?? "").length;
      try { el.setSelectionRange(len, len); } catch {}
    }
  }, [editing]);

  const enter = (e) => {
    if (disabled) return;
    e?.stopPropagation?.();
    e?.preventDefault?.();
    setDraft(value ?? "");
    setEditing(true);
  };

  const commit = async () => {
    if (saving) return;
    const next = (draft ?? "").toString();
    const prev = (value ?? "").toString();
    if (next === prev) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await onSave?.(next);
      if (saveNotice) toast(saveNotice);
    } catch (err) {
      toast("Save failed", { description: err?.response?.data?.detail || err?.message || "" });
      setDraft(prev);
    } finally {
      setSaving(false);
      setEditing(false);
    }
  };

  const cancel = () => {
    setDraft(value ?? "");
    setEditing(false);
  };

  const onKey = (e) => {
    if (e.key === "Escape") { e.preventDefault(); cancel(); return; }
    if (!multiline && e.key === "Enter") { e.preventDefault(); commit(); return; }
    if (multiline && e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); commit(); return; }
  };

  if (editing) {
    const commonProps = {
      ref,
      value: draft,
      onChange: (e) => setDraft(e.target.value),
      onBlur: commit,
      onKeyDown: onKey,
      "data-testid": testid ? `${testid}-input` : undefined,
      "aria-label": "Edit text",
      className: `op-editable-input ${inputClassName}`,
      placeholder,
      onClick: (e) => e.stopPropagation(),
    };
    return multiline ? (
      <textarea rows={3} {...commonProps} />
    ) : (
      <input type="text" {...commonProps} />
    );
  }

  const display = (value == null || value === "")
    ? emptyPlaceholder
    : (displayTransform ? displayTransform(value) : value);

  const outlineCls = editMode && !disabled ? "op-editable--armed" : "op-editable--idle";

  return (
    <Tag
      data-testid={testid}
      onDoubleClick={enter}
      onClick={editMode && !disabled ? enter : undefined}
      className={`op-editable ${outlineCls} ${disabled ? "" : "cursor-text"} ${className}`}
      title={disabled ? undefined : "Double-click to edit"}
    >
      <span className="op-editable__content">{display}</span>
      {!disabled && (
        <button
          type="button"
          onClick={enter}
          tabIndex={-1}
          className="op-editable__pencil"
          data-testid={testid ? `${testid}-pencil` : undefined}
          aria-label="Edit"
        >
          <Pencil size={10} strokeWidth={1.6} />
        </button>
      )}
    </Tag>
  );
};

export default EditableText;
